import React from 'react';
import { Smartphone, Zap, Shield, TrendingUp, CheckCircle, Eye, Clock, Users, Target, BarChart3 } from 'lucide-react';
// @ts-ignore: module has no declaration file
import useDynamicSEO from '../hooks/dynamicSEO';

const DigitalParkingSolutions: React.FC = () => {
    useDynamicSEO({
      title: "Digital Parking Solutions | Complete Automation & Smart App",
      description: "Modern Digital Parking Solutions designed to eliminate manual processes, accelerate movement, & provide fully digital billing, payments & precise reporting.",
          keywords:["Parking Management System","Event & Exhibition Parking Management","Parking Lot Management App","Digital Parking Solutions"," Vehicle entry exit management system","AI Parking Management Platform","Parking Lot Automation Software", "Parking revenue management app", "Tourist Attraction Parking Software", "Cinema & Multiplex Parking System"],
      ogTitle: "Digital Parking Solutions | Complete Automation & Smart App",
      ogDescription: "Modern Digital Parking Solutions designed to eliminate manual processes, accelerate movement, & provide fully digital billing, payments & precise reporting."
    });

  const handleWhatsAppDemo = () => {
    const phoneNumber = "919384376329";
    const message = "Hi! I'm interested in learning more about Park Hive's Digital Parking Solutions. Can you please provide me with a demo?";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const digitalFeatures = [
    "Access control and slot allocation",
    "Payment processing automation",
    "Real-time monitoring and reporting",
    "Data-driven decision making",
    "Complete transparency",
    "Intelligent operation management"
  ];

  const expectations = [
    {
      type: "customers",
      title: "Customer Expectations",
      items: [
        "Fast entry without delays",
        "Zero waiting at exit",
        "Accurate billing transparency",
        "Real-time information access"
      ],
      color: "blue"
    },
    {
      type: "owners",
      title: "Owner Requirements", 
      items: [
        "Complete operational visibility",
        "Revenue accuracy guarantee",
        "Trustable staff accountability",
        "Real-time control systems"
      ],
      color: "green"
    }
  ];

  const industries = [
    "Malls and multiplexes",
    "Corporate offices",
    "Hospitals and healthcare",
    "Universities and schools",
    "Airports and transport hubs",
    "Residential communities",
    "Event venues and stadiums",
    "Tourist attractions",
    "Temporary outdoor parking"
  ];

  const transformations = [
    { before: "Queues pile up", after: "Queues shrink" },
    { before: "Complaints increase", after: "Complaints disappear" },
    { before: "Managers rush around", after: "Managers stop rushing" },
    { before: "Staff lack accountability", after: "Staff become responsible" },
    { before: "Customers leave frustrated", after: "Customers walk away satisfied" },
    { before: "Revenue leaks", after: "Revenue increases transparently" }
  ];

  const faqs = [
    {
      question: "Do I need sensors or hardware to start?",
      answer: "No — Park Hive works independently through the mobile app and can integrate with hardware when required."
    },
    {
      question: "Can digital parking handle heavy traffic events and peak loads?",
      answer: "Yes — it is engineered for high-volume, time-critical operations."
    },
    {
      question: "Is training needed for staff?",
      answer: "Minimal — the interface is simple enough for anyone to learn within minutes."
    },
    {
      question: "Can I access reports and monitor revenue remotely?",
      answer: "Yes — all activity updates live on the dashboard from anywhere."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-indigo-50 to-blue-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Digital Parking <span className="text-blue-600">Solutions</span>
            </h1>
            <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-8">
              Transform Your Parking Space with Digital Parking Solutions Designed for Speed, Accuracy & Intelligent Control
            </h2>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Parking is no longer a simple storage space for vehicles. It has become a critical service touchpoint — 
                the first and last experience every visitor has with your property.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-bold text-green-900">✅ When parking moves smoothly</h4>
                  <p className="text-green-700">People feel valued</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-bold text-red-900">❌ When it doesn't</h4>
                  <p className="text-red-700">Frustration spreads faster than traffic</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-red-100 to-orange-100 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-bold text-red-900 mb-4">The Manual Parking Crisis</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-red-800">
                <div>📄 Paper slips fail</div>
                <div>👥 Staff dependency increases risk</div>
                <div>💸 Revenue leakage threatens profits</div>
              </div>
              <p className="mt-4 font-medium">Facilities that stay manual fall behind — losing customers, losing control, and losing profit.</p>
            </div>
            
            <div className="bg-blue-600 text-white rounded-xl p-8 mb-8">
              <h3 className="text-2xl font-bold mb-4">The Digital Evolution</h3>
              <p className="text-xl">
                Park Hive delivers the evolution modern parking demands with powerful Digital Parking Solutions 
                that bring complete automation, transparency, and efficiency into the heart of parking operations.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Request a Live Demo
              </button>
              <button 
                onClick={handleWhatsAppDemo}
                className="border border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-50 transition-colors duration-200"
              >
                Request a Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What Are Digital Parking Solutions */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              What Are Digital Parking Solutions?
            </h2>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-8 mb-12">
              <p className="text-lg text-gray-700 leading-relaxed text-center mb-8">
                Digital Parking Solutions replace outdated manual processes with intelligent systems that automate 
                every step of the parking journey
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {digitalFeatures.map((feature, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 shadow-sm text-center">
                    <CheckCircle className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                    <p className="font-medium text-gray-900">{feature}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
                <Target className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Instead of managing chaos</h4>
                <p className="text-gray-600">Owners manage clarity</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
                <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Instead of reacting to problems</h4>
                <p className="text-gray-600">They prevent them</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
                <BarChart3 className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Instead of guessing</h4>
                <p className="text-gray-600">They measure</p>
              </div>
            </div>
            
            <div className="text-center bg-gradient-to-r from-gray-900 to-blue-900 text-white rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-2">Technology becomes the operating engine</h3>
              <p className="text-xl">Not manpower.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Digital Transformation Now */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Why Parking Owners Need Digital Transformation Now
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {expectations.map((expectation, index) => (
                <div key={index} className={`bg-${expectation.color}-50 border border-${expectation.color}-200 rounded-xl p-6`}>
                  <h4 className={`text-xl font-bold text-${expectation.color}-900 mb-4`}>{expectation.title}</h4>
                  <ul className={`space-y-2 text-${expectation.color}-800`}>
                    {expectation.items.map((item, idx) => (
                      <li key={idx} className="flex items-center">
                        <CheckCircle className={`h-5 w-5 text-${expectation.color}-600 mr-3 flex-shrink-0`} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">
                Digital solutions deliver all of it
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div className="text-center">
                  <Smartphone className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                  <p className="font-medium">Smartphone command center</p>
                </div>
                <div className="text-center">
                  <Eye className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  <p className="font-medium">Every movement recorded</p>
                </div>
                <div className="text-center">
                  <Shield className="h-12 w-12 text-purple-600 mx-auto mb-3" />
                  <p className="font-medium">Every transaction transparent</p>
                </div>
                <div className="text-center">
                  <Clock className="h-12 w-12 text-orange-600 mx-auto mb-3" />
                  <p className="font-medium">Every second accounted</p>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-lg text-gray-700">
                  <strong>No expensive hardware setups or lengthy installations required</strong>
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-orange-100 to-red-100 rounded-xl p-6 text-center">
              <h4 className="text-xl font-bold text-red-900 mb-2">Digital parking is not a trend.</h4>
              <p className="text-red-800 text-lg">
                It is the future standard — and adopting it now protects your position before competition arrives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Park Hive Powers Digital Operations */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              How Park Hive Powers Digital Parking Operations
            </h2>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-8 mb-12">
              <p className="text-lg text-gray-700 text-center mb-8 leading-relaxed">
                Park Hive turns any parking lot into a smart, connected ecosystem. Through a secure mobile platform, 
                operators can create and manage parking zones, speed up entry and exit with scanning technology, 
                track daily collections, monitor occupancy live, and generate automated reports.
              </p>
            </div>
            
            <div className="space-y-6 mb-12">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 flex items-center">
                <Zap className="h-8 w-8 text-blue-600 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-blue-900">Vehicles enter with instant scanning and accurate time tracking</h4>
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 flex items-center">
                <TrendingUp className="h-8 w-8 text-green-600 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-green-900">Pricing is calculated based on smart rules — hourly, daily, weekend, or custom</h4>
                </div>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 flex items-center">
                <Shield className="h-8 w-8 text-purple-600 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-purple-900">Payments are digital and frictionless</h4>
                </div>
              </div>
              
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 flex items-center">
                <Users className="h-8 w-8 text-orange-600 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-orange-900">Staff work with traceable accountability</h4>
                </div>
              </div>
              
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 flex items-center">
                <Eye className="h-8 w-8 text-indigo-600 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-indigo-900">Owners get complete real-time control</h4>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                No more lost receipts, false records, or hidden revenue
              </h3>
              <p className="text-xl text-gray-700">
                Everything is visible. Everything is measurable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Built for Every Type */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Built for Every Type of Parking Operation
            </h2>
            
            <p className="text-lg text-gray-600 text-center mb-12">
              Digital Parking Solutions empower parking lots across industries
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
              {industries.map((industry, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-sm flex items-center">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{industry}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Scale Without Limits
              </h3>
              <p className="text-lg text-gray-700 mb-6">
                Whether managing 50 vehicles or 50,000, consistency remains the same — 
                faster movement, smoother service, and stronger profitability.
              </p>
              
              <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-2">Digital parking doesn't just fix operations.</h4>
                <p className="text-lg text-gray-700">It elevates customer confidence and property brand value.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real Impact */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Real Impact for Parking Owners
            </h2>
            
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8 mb-12">
              <p className="text-lg text-gray-700 text-center mb-8 leading-relaxed">
                The moment technology replaces manual dependence, operations transform permanently.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {transformations.map((transformation, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-red-600 mr-3">❌</span>
                      <span className="text-gray-700">{transformation.before}</span>
                    </div>
                    <div className="mx-4">→</div>
                    <div className="flex items-center">
                      <span className="text-green-600 mr-3">✅</span>
                      <span className="text-gray-900 font-medium">{transformation.after}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Digital parking is not just an upgrade.</h3>
              <p className="text-xl">It is a business accelerator.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Park Hive */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">
              Why Park Hive
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white rounded-xl p-8 shadow-md">
                <h4 className="text-xl font-bold text-gray-900 mb-4">🎯 Purpose-Built</h4>
                <ul className="text-gray-700 space-y-2 text-left">
                  <li>• Because owners deserve clarity</li>
                  <li>• Because staff need structure</li>
                  <li>• Because customers demand speed</li>
                  <li>• Because revenue must be protected</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-md">
                <h4 className="text-xl font-bold text-gray-900 mb-4">⚡ Smart Design</h4>
                <ul className="text-gray-700 space-y-2 text-left">
                  <li>• Without complex infrastructure</li>
                  <li>• Without high cost</li>
                  <li>• Without exhausting management</li>
                  <li>• Without technical complexity</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Park Hive is built to help parking owners regain control
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-bold text-blue-600 mb-2">Simple setup</h4>
                  <p className="text-sm text-gray-600">Quick implementation</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-bold text-green-600 mb-2">Powerful intelligence</h4>
                  <p className="text-sm text-gray-600">Advanced capabilities</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-bold text-purple-600 mb-2">Future-ready system</h4>
                  <p className="text-sm text-gray-600">Grows with your business</p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Request a Live Demo
              </button>
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-green-700 transition-colors duration-200"
              >
                Request a Demo
              </button>
            </div>
            
            <p className="text-gray-600 mt-6">
              See how fast and smooth modern parking can be.<br />
              Watch real workflows. Feel real-time control. Understand real financial visibility.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h4>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final Thought */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-12">Final Thought</h2>
            
            <div className="space-y-8 text-lg leading-relaxed">
              <p className="text-xl">Digital parking is more than technology —</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-800/30 rounded-lg p-6">
                  <h4 className="font-bold text-blue-300 mb-2">🧠 Peace of mind</h4>
                  <p>for owners</p>
                </div>
                <div className="bg-green-800/30 rounded-lg p-6">
                  <h4 className="font-bold text-green-300 mb-2">⚡ Speed</h4>
                  <p>for customers</p>
                </div>
                <div className="bg-purple-800/30 rounded-lg p-6">
                  <h4 className="font-bold text-purple-300 mb-2">🔍 Transparency</h4>
                  <p>for operations</p>
                </div>
                <div className="bg-orange-800/30 rounded-lg p-6">
                  <h4 className="font-bold text-orange-300 mb-2">🏆 Reputation</h4>
                  <p>for the property</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <p className="text-xl font-bold text-yellow-300">
                  The future will belong to those who move first.
                </p>
                <p className="text-2xl font-bold">
                  Park Hive delivers that future — today
                </p>
              </div>
            </div>
            
            <div className="mt-12">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 transition-colors duration-200 inline-flex items-center"
              >
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z"/>
                </svg>
                Turn your parking space into a smarter, safer, more profitable operation
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DigitalParkingSolutions;